import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toISODateSafe } from '../../utils/dateUtils';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DatosEficienciaCombustible } from '../../services/gestionService';

interface GraficoBurbujaProps {
  datos: DatosEficienciaCombustible[];
  isLoading: boolean;
  maquinas?: Array<{ id: number; codigo: string; descripcion: string }>;
  operadores?: Array<{ id: number; codigo: string; descripcion: string }>;
  faenas?: Array<{ id: number; codigo: string; descripcion: string }>;
  onFiltroMaquinaChange?: (maquinaId: number | null) => void;
  onFiltroOperadorChange?: (operadorId: number | null) => void;
  onFiltroFaenaChange?: (faenaId: number | null) => void;
}

export const GraficoBurbuja: React.FC<GraficoBurbujaProps> = ({
  datos,
  isLoading,
  maquinas: _maquinas = [],
  operadores: _operadores = [],
  faenas: _faenas = [],
  onFiltroMaquinaChange,
  onFiltroOperadorChange,
  onFiltroFaenaChange,
}) => {
  const { t } = useTranslation();
  const [filtroMaquina, setFiltroMaquina] = useState<string | null>(null);
  const [filtroOperador, setFiltroOperador] = useState<string | null>(null);
  const [filtroFaena, setFiltroFaena] = useState<string | null>(null);
  const [tipoColoreo, setTipoColoreo] = useState<'maquina' | 'operador' | 'eficiencia'>('maquina');

  // Estado para fechas
  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');

  // Inicializar fechas con rango de últimos 90 días
  React.useEffect(() => {
    const hoy = new Date();
    const hace90Dias = new Date();
    hace90Dias.setDate(hace90Dias.getDate() - 90);

    setFechaDesde(toISODateSafe(hace90Dias));
    setFechaHasta(toISODateSafe(hoy));
  }, []);

  const colores = [
    '#3b82f6', // blue
    '#ef4444', // red
    '#10b981', // green
    '#f59e0b', // amber
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316', // orange
  ];

  // Procesar datos para el gráfico
  const datosGrafico = useMemo(() => {
    if (!datos) return [];

    let datosProcessados = [...datos];

    // Aplicar filtros de fechas
    if (fechaDesde) {
      datosProcessados = datosProcessados.filter((d) => d.fecha >= fechaDesde);
    }
    if (fechaHasta) {
      datosProcessados = datosProcessados.filter((d) => d.fecha <= fechaHasta);
    }

    // Aplicar filtros
    if (filtroMaquina) {
      datosProcessados = datosProcessados.filter((d) => d.maquina_codigo === filtroMaquina);
    }
    if (filtroOperador) {
      datosProcessados = datosProcessados.filter((d) => d.operador_codigo === filtroOperador);
    }
    if (filtroFaena) {
      datosProcessados = datosProcessados.filter((d) => d.faena_codigo === filtroFaena);
    }

    // Crear mapa de índices para coloreo consistente
    const maquinasUnicas = Array.from(new Set(datos.map((d) => d.maquina_codigo)));
    const operadoresUnicos = Array.from(new Set(datos.map((d) => d.operador_codigo)));

    // Transformar para gráfico de burbuja
    return datosProcessados.map((d) => {
      let color = '#3b82f6'; // default blue

      if (tipoColoreo === 'maquina') {
        const maquinaIndex = maquinasUnicas.indexOf(d.maquina_codigo);
        color = colores[maquinaIndex % colores.length];
      } else if (tipoColoreo === 'operador') {
        const operadorIndex = operadoresUnicos.indexOf(d.operador_codigo);
        color = colores[operadorIndex % colores.length];
      } else if (tipoColoreo === 'eficiencia') {
        // Colorear por eficiencia: rojo (malo) a verde (bueno)
        if (d.consumo_por_hora > 3.5) color = '#ef4444'; // red - mala eficiencia
        else if (d.consumo_por_hora > 2.5) color = '#f59e0b'; // amber
        else if (d.consumo_por_hora > 1.5) color = '#10b981'; // green - buena eficiencia
        else color = '#06b6d4'; // cyan - excelente
      }

      return {
        x: d.horas_trabajadas,
        y: d.consumo_petroleo,
        z: d.area_trabajada > 0 ? Math.max(50, d.area_trabajada * 50) : 50, // tamaño burbuja, mínimo 50
        reporte_id: d.reporte_id,
        fecha: d.fecha,
        maquina: d.maquina_descripcion,
        maquina_codigo: d.maquina_codigo,
        operador: d.operador_descripcion,
        operador_codigo: d.operador_codigo,
        faena: d.faena_descripcion,
        faena_codigo: d.faena_codigo,
        horas: d.horas_trabajadas,
        consumo: d.consumo_petroleo,
        area: d.area_trabajada,
        consumo_por_hora: d.consumo_por_hora,
        color,
      };
    });
  }, [datos, filtroMaquina, filtroOperador, filtroFaena, tipoColoreo, colores, fechaDesde, fechaHasta]);

  // Calcular estadísticas
  const estadisticas = useMemo(() => {
    if (datosGrafico.length === 0) {
      return {
        puntos: 0,
        horasPromedio: 0,
        consumoPromedio: 0,
        eficienciaPromedio: 0,
        mejorEficiencia: 0,
        peorEficiencia: 0,
      };
    }

    const horasPromedio =
      datosGrafico.reduce((sum, d) => sum + d.horas, 0) / datosGrafico.length;
    const consumoPromedio =
      datosGrafico.reduce((sum, d) => sum + d.consumo, 0) / datosGrafico.length;
    const eficienciaPromedio =
      datosGrafico.reduce((sum, d) => sum + d.consumo_por_hora, 0) / datosGrafico.length;
    const mejorEficiencia = Math.min(...datosGrafico.map((d) => d.consumo_por_hora));
    const peorEficiencia = Math.max(...datosGrafico.map((d) => d.consumo_por_hora));

    return {
      puntos: datosGrafico.length,
      horasPromedio: Math.round(horasPromedio * 100) / 100,
      consumoPromedio: Math.round(consumoPromedio * 100) / 100,
      eficienciaPromedio: Math.round(eficienciaPromedio * 100) / 100,
      mejorEficiencia: Math.round(mejorEficiencia * 100) / 100,
      peorEficiencia: Math.round(peorEficiencia * 100) / 100,
    };
  }, [datosGrafico]);

  const handleFiltroMaquina = (codigo: string | null) => {
    setFiltroMaquina(codigo);
    // onFiltroMaquinaChange es opcional, solo para cuando se use en contexto padre
    onFiltroMaquinaChange?.(codigo ? 1 : null); // dummy ID
  };

  const handleFiltroOperador = (codigo: string | null) => {
    setFiltroOperador(codigo);
    onFiltroOperadorChange?.(codigo ? 1 : null); // dummy ID
  };

  const handleFiltroFaena = (codigo: string | null) => {
    setFiltroFaena(codigo);
    onFiltroFaenaChange?.(codigo ? 1 : null); // dummy ID
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  if (datos.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="text-gray-400">{t('common.no_data')}</div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        minWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
    >
      {/* Panel de Filtros */}
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '1rem',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          alignItems: 'center',
        }}
      >
        {/* Selector de Período */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', borderRight: '1px solid #e5e7eb', paddingRight: '1rem' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
            {t('analysis.date_from')}:
          </label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              color: '#111827',
              fontSize: '0.875rem',
              cursor: 'pointer',
            }}
          />
          <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
            {t('analysis.date_to')}:
          </label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              color: '#111827',
              fontSize: '0.875rem',
              cursor: 'pointer',
            }}
          />
        </div>
        {/* Filtro Máquina */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
            {t('reports.machine')}:
          </label>
          <select
            value={filtroMaquina || ''}
            onChange={(e) => handleFiltroMaquina(e.target.value || null)}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              color: '#111827',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <option value="">{t('common.all')}</option>
            {Array.from(new Set(datos.map((d) => d.maquina_codigo))).map((codigo) => {
              const maquina = datos.find((d) => d.maquina_codigo === codigo);
              return (
                <option key={codigo} value={codigo}>
                  {maquina?.maquina_descripcion}
                </option>
              );
            })}
          </select>
        </div>

        {/* Filtro Operador */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
            {t('reports.operator')}:
          </label>
          <select
            value={filtroOperador || ''}
            onChange={(e) => handleFiltroOperador(e.target.value || null)}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              color: '#111827',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <option value="">{t('common.all')}</option>
            {Array.from(new Set(datos.map((d) => d.operador_codigo))).map((codigo) => {
              const operador = datos.find((d) => d.operador_codigo === codigo);
              return (
                <option key={codigo} value={codigo}>
                  {operador?.operador_descripcion}
                </option>
              );
            })}
          </select>
        </div>

        {/* Filtro Faena */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
            {t('analysis.faenas')}:
          </label>
          <select
            value={filtroFaena || ''}
            onChange={(e) => handleFiltroFaena(e.target.value || null)}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              color: '#111827',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <option value="">{t('common.all')}</option>
            {Array.from(new Set(datos.map((d) => d.faena_codigo))).map((codigo) => {
              const faena = datos.find((d) => d.faena_codigo === codigo);
              return (
                <option key={codigo} value={codigo}>
                  {faena?.faena_descripcion}
                </option>
              );
            })}
          </select>
        </div>

        {/* Selector de Coloreo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
            {t('management.color_by')}:
          </label>
          <select
            value={tipoColoreo}
            onChange={(e) => setTipoColoreo(e.target.value as 'maquina' | 'operador' | 'eficiencia')}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              color: '#111827',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <option value="maquina">{t('reports.machine')}</option>
            <option value="operador">{t('reports.operator')}</option>
            <option value="eficiencia">{t('management.efficiency')}</option>
          </select>
        </div>
      </div>

      {/* Tarjetas de Estadísticas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>{t('analysis.total_reports')}</p>
          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>
            {estadisticas.puntos}
          </p>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>{t('management.avg_prefix')} {t('analysis.total_hours')}</p>
          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>
            {estadisticas.horasPromedio}h
          </p>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>{t('management.consumption')} {t('management.avg_prefix')}</p>
          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>
            {estadisticas.consumoPromedio}L
          </p>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>{t('management.avg_efficiency')} ({t('management.fuel')})</p>
          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>
            {estadisticas.eficienciaPromedio}L/h
          </p>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>{t('management.best_efficiency')}</p>

          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
            {estadisticas.mejorEficiencia}L/h
          </p>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>{t('management.worst_efficiency')}</p>
          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>
            {estadisticas.peorEficiencia}L/h
          </p>
        </div>
      </div>

      {/* Gráfico de Burbuja */}
      <div
        className="bg-white rounded-lg shadow p-6"
        style={{ width: '100%', height: '700px', minHeight: '700px', maxHeight: '700px' }}
      >
        {datosGrafico.length > 0 ? (
          <>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
              Eficiencia de {t('management.fuel')}
            </h3>
            <ResponsiveContainer width="100%" height={600}>
              <ScatterChart margin={{ top: 20, right: 20, bottom: 70, left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="x"
                  type="number"
                  name={t('analysis.total_hours')}
                  label={{
                    value: t('analysis.total_hours'),
                    position: 'insideBottom',
                    offset: -10,
                  }}
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                />
                <YAxis
                  dataKey="y"
                  type="number"
                  name={`${t('management.consumption')} (L)`}
                  label={{
                    value: `${t('management.consumption')} de PETROLEO (litros)`,
                    angle: -90,
                    position: 'insideLeft',
                  }}
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  formatter={(value: any) => {
                    if (typeof value === 'number') {
                      return value.toFixed(2);
                    }
                    return value;
                  }}
                  labelFormatter={(label: any) => {
                    const punto = datosGrafico.find((d) => d.x === label);
                    return punto
                      ? `${punto.maquina} - ${punto.operador} (${punto.fecha})`
                      : '';
                  }}
                  content={({ active, payload }: { active?: boolean; payload?: any[] }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div
                          style={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '8px',
                            fontSize: '12px',
                          }}
                        >
                          <p style={{ margin: '2px 0', fontWeight: 'bold' }}>
                            {data.maquina} - {data.operador}
                          </p>
                          <p style={{ margin: '2px 0', color: '#6b7280' }}>
                            {t('reports.date')}: {data.fecha}
                          </p>
                          <p style={{ margin: '2px 0' }}>
                            {t('analysis.total_hours')}: {data.horas.toFixed(1)}h
                          </p>
                          <p style={{ margin: '2px 0' }}>
                            {t('management.consumption')}: {data.consumo.toFixed(1)}L
                          </p>
                          <p style={{ margin: '2px 0' }}>
                            {t('common.area')}: {data.area.toFixed(2)}ha
                          </p>
                          <p style={{ margin: '2px 0', fontWeight: 'bold', color: '#2563eb' }}>
                            {t('management.efficiency')}: {data.consumo_por_hora.toFixed(1)}L/h
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Scatter
                  name={t('analysis.total_reports')}
                  data={datosGrafico}
                  fill="#8884d8"
                  shape={
                    tipoColoreo === 'eficiencia'
                      ? undefined
                      : ({ cx, cy, payload }: any) => (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={Math.sqrt(payload.z) / 2}
                            fill={payload.color}
                            opacity={0.7}
                            stroke={payload.color}
                            strokeWidth={2}
                          />
                        )
                  }
                />
                {tipoColoreo === 'eficiencia' && (
                  <Scatter
                    name={`${t('analysis.total_reports')} (por eficiencia)`}
                    data={datosGrafico}
                    fill="#8884d8"
                    shape={({ cx, cy, payload }: any) => (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={Math.sqrt(payload.z) / 2}
                        fill={payload.color}
                        opacity={0.7}
                        stroke={payload.color}
                        strokeWidth={2}
                      />
                    )}
                  />
                )}
              </ScatterChart>
            </ResponsiveContainer>

            {/* Leyenda */}
            <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
              <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{t('management.interpretation')}:</p>
              <ul style={{ marginLeft: '1rem', listStyle: 'disc' }}>
                <li>{t('management.chart_top_right')}</li>
                <li>{t('management.chart_bottom_right')}</li>
                <li>{t('management.bubble_size')}</li>
                {tipoColoreo === 'eficiencia' && (
                  <>
                    <li><strong style={{ color: '#06b6d4' }}>{t('management.color_cyan')}</strong></li>
                    <li><strong style={{ color: '#10b981' }}>{t('management.color_green')}</strong></li>
                    <li><strong style={{ color: '#f59e0b' }}>{t('management.color_amber')}</strong></li>
                    <li><strong style={{ color: '#ef4444' }}>{t('management.color_red')}</strong></li>
                  </>
                )}
              </ul>
            </div>
          </>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#9ca3af',
            }}
          >
            {t('common.no_results')}
          </div>
        )}
      </div>
    </div>
  );
};
